/**
 * The signed-in landing page: today's session first (`TodayCard`, pedagogy
 * spec §12 D1), then one compact quick link per top-bar item (the library's
 * three entries collapse into a single link, exactly as they do in the
 * shell), each with a live count when the count is cheap to fetch.
 *
 * "Cheap" means a small, cacheable GET the student is likely to need anyway:
 * `/api/content/topics` (bank counts), `/api/content/old-exams` (the archive
 * index) and `/api/flashcards/decks` (per-chapter due counts). The heavy
 * `/api/progress/overview` is not called from here, except by the Tentaläge
 * card (slice D) while Tentaläge is on; the one extra read otherwise is
 * `GET /api/practice/today`, made by `TodayCard` itself.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router";
import { api } from "@client/api/client";
import { useTopicsAndChapters } from "@client/api/topics";
import { useAuth } from "@client/app/auth";
import { navGroups, visibleNav, type NavGroup } from "@client/app/features";
import { useExamMode } from "@client/app/examMode";
import { PageHeader } from "@client/components/ui";
import { LectureCard } from "@client/features/content/LectureCard";
import { examKeys } from "@client/features/exam/api";
import { ResumeCard } from "@client/features/practice/ResumeCard";
import { TodayCard } from "@client/features/practice/today";
import { useLang, useT, useTp } from "@client/i18n";
import { isCode } from "@client/lib/errors";
import { formatNumber } from "@client/lib/format";
import { ExamModeCard } from "./ExamModeCard";
import { KomIgangCard } from "./onboarding/KomIgangCard";

function useHomeCounts() {
  const bank = useTopicsAndChapters();
  // Same keys the content and flashcards features use, so the home page warms
  // their caches instead of duplicating the fetch.
  const papers = useQuery({
    queryKey: ["content", "old-exams"] as const,
    queryFn: () => api.get<{ exams: unknown[] }>("/api/content/old-exams"),
    staleTime: 5 * 60_000,
  });
  const decks = useQuery({
    queryKey: ["flashcards", "decks"] as const,
    queryFn: () => api.get<{ decks: { due: number; newCards: number }[] }>("/api/flashcards/decks"),
  });
  // A leaf of its own under the exam prefix (review-A minor), not the exam
  // feature's own `examKeys.current()` (`["exam", "current"]`): sharing that
  // exact key seeded its cache with a `null` result before any exam existed,
  // and `ExamSessionPage` treats *cached* `null` data as "definitely no
  // session" and redirects away — it does not wait for the background
  // refetch a shared, invalidated key would still need a moment to
  // complete. Nesting it under `examKeys.all` still lets exam actions
  // (start/submit/grade) invalidate this banner by their existing prefix
  // invalidation, without the two ever sharing one cache entry. 404 (no
  // session) is the normal, expected outcome here, not an error to surface.
  const activeExam = useQuery({
    queryKey: [...examKeys.all, "home-active"] as const,
    queryFn: async () => {
      try {
        return await api.get<{ session: { id: string } }>("/api/exam/current");
      } catch (error) {
        if (isCode(error, "NOT_FOUND")) return null;
        throw error;
      }
    },
  });

  return {
    counts: bank.data?.counts,
    pastExams: papers.data?.exams.length,
    cardsDue: decks.data?.decks.reduce((sum, deck) => sum + deck.due, 0),
    // A brand-new account has nothing due and the whole bank unseen. Without
    // this the card read "0 kort att repetera" over 168 waiting cards and the
    // deck looked empty.
    cardsNew: decks.data?.decks.reduce((sum, deck) => sum + deck.newCards, 0),
    activeExam: activeExam.data?.session ?? null,
  };
}

export function HomePage() {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const { user } = useAuth();
  const { examActive } = useExamMode();
  const { counts, pastExams, cardsDue, cardsNew, activeExam } = useHomeCounts();

  const groups = useMemo(
    () => navGroups(visibleNav({ role: user?.role, examActive })),
    [user?.role, examActive],
  );

  /** The one-line count under a card's title, or undefined when we have none. */
  function caption(group: NavGroup): string | undefined {
    const n = (value: number) => formatNumber(value, lang);
    switch (group.key) {
      case "/practice":
        return counts
          ? t("home.count.practice", { count: n(counts.selectableQuestions) })
          : undefined;
      case "/exam":
        return pastExams === undefined ? undefined : t("home.count.exam", { count: n(pastExams) });
      case "/flashcards": {
        if (cardsDue === undefined) return undefined;
        const due = tp("home.count.flashcards.due", cardsDue, { count: n(cardsDue) });
        if (!cardsNew) return due;
        return `${due} · ${tp("home.count.flashcards.new", cardsNew, { count: n(cardsNew) })}`;
      }
      case "content.nav.library":
        return counts
          ? t("home.count.library", { topics: n(counts.topics), formulas: n(counts.formulas) })
          : undefined;
      default:
        return undefined;
    }
  }

  return (
    <>
      <PageHeader
        title={t("home.greeting", { name: user?.displayName ?? "" })}
        description={t("home.subtitle")}
      />
      {/*
        UX-08/UX-31: Home said "4 gamla tentor att skriva" with no hint that
        one of those "old exams" was in fact the student's own running
        session — a mis-tap on a duration preset then had no easy way back.
      */}
      {activeExam && (
        <p
          role="status"
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900 dark:border-accent-700 dark:bg-accent-950 dark:text-accent-100"
        >
          <span>
            <strong className="font-medium">{t("home.activeExam.title")}</strong>{" "}
            {t("home.activeExam.body")}
          </span>
          <Link
            to="/exam/session"
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            {t("home.activeExam.link")}
          </Link>
        </p>
      )}
      {/* Slice C, P1-11: four light questions for a student who has not answered them yet. */}
      <KomIgangCard />
      {/* Today comes first (pedagogy spec §12 D1); the quick links become a compact row below. */}
      <ResumeCard />
      {/* Tentaläge (slice D, P2-1): 14 days before the exam, or when the student repeterar. */}
      <ExamModeCard />
      {/* Where the course is (slice B, P1-1): the next lecture, above the day's session. */}
      <LectureCard />
      <TodayCard />
      <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
        {t("home.quickLinks")}
      </h2>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map((group) => {
          const Icon = group.icon;
          const description = caption(group);
          const cardClass =
            "surface block h-full px-3 py-2.5 transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500";
          const heading = (
            <div className="flex items-center gap-2 text-sm font-medium text-ink-900 dark:text-ink-50">
              {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
              {t(group.labelKey)}
            </div>
          );
          const captionLine = description && (
            <div className="mt-0.5 text-xs text-ink-600 dark:text-ink-400">{description}</div>
          );
          // A grouped card holds one link per entry (P0-4): links cannot nest, so the card is a
          // plain box with a heading link and the entries as sibling links.
          if (group.grouped) {
            return (
              <div key={group.key} className="surface h-full px-3 py-2.5">
                <Link
                  to={group.to}
                  className="block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
                >
                  {heading}
                </Link>
                {captionLine}
                <ul className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                  {group.entries.map((entry) => (
                    <li key={entry.to}>
                      <Link
                        to={entry.to}
                        className="text-accent-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:text-accent-400"
                      >
                        {t(entry.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          return (
            <Link key={group.key} to={group.to} className={cardClass}>
              {heading}
              {captionLine}
            </Link>
          );
        })}
      </div>
    </>
  );
}

export default HomePage;
